import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader, AlertCircle, ExternalLink, Sparkles } from 'lucide-react';
import { useDocketMetadata } from '../hooks/useDocketMetadata';
import { SummariesSection } from '../components/proceedings/SummariesSection';
import { formatDeadlineCalendarDate } from '../utils/nprmHelpers';

/**
 * Standalone Comment Summaries workflow page.
 *
 * Shares all docket metadata loading (proceeding row, NPRM/PN counts,
 * deadlines, prevailing instrument, synthetic PN timeline) with
 * ProceedingDetailsPage via `useDocketMetadata`, so this view stays in
 * lock-step with the equivalent tab on /proceedings/:n.
 *
 * Auto-enroll / parallel upload is intentionally NOT triggered here —
 * that side effect is owned by ProceedingDetailsPage. SummariesSection
 * still loads filings + NPRM documents (via its own effects) regardless,
 * so the form works fine on dockets that haven't been auto-enrolled yet.
 */
export const CommentSummariesPage: React.FC = () => {
  const { docketNumber } = useParams<{ docketNumber: string }>();
  const {
    loading,
    error,
    proceeding,
    nprmCount,
    fetchingNprmCount,
    publicNoticeCount,
    topPublicNoticeDocument,
    commentDeadline,
    replyCommentDeadline,
    prevailingInstrument,
    syntheticPnTimelineForFilings,
  } = useDocketMetadata(docketNumber);
  // commentDeadlineExtension / replyDeadlineExtension are surfaced on the
  // ProceedingDetailHeader (used by /proceedings/:docket). This page
  // doesn't render that header — the extension footer is intentionally
  // only on the main proceedings page where attorneys land first.

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="h-6 w-6 animate-spin text-cg-green" />
        <span className="ml-3 text-gray-600">Loading docket…</span>
      </div>
    );
  }

  if (error || !proceeding || !docketNumber) {
    return (
      <div className="mx-auto max-w-2xl py-12">
        <Link
          to="/comment-summaries"
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to picker
        </Link>
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
          <div className="text-sm text-red-700">
            {error ?? `Proceeding "${docketNumber}" not found.`}
          </div>
        </div>
      </div>
    );
  }

  const commentDeadlineLabel = commentDeadline
    ? formatDeadlineCalendarDate(commentDeadline.toISOString())
    : null;
  const replyDeadlineLabel = replyCommentDeadline
    ? formatDeadlineCalendarDate(replyCommentDeadline.toISOString())
    : null;

  return (
    <div className="mx-auto max-w-5xl">
      {/* Flat header — no wrapping card. The SummariesSection below is the
          page's primary visual unit; stacking another bordered card around
          this metadata made everything feel nested. */}
      <Link
        to="/comment-summaries"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-800"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to picker
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="mb-2 flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.1em] text-cg-green">
            <Sparkles className="h-3.5 w-3.5" />
            Comment Summaries
          </div>
          <div className="flex flex-wrap items-baseline gap-3">
            <h1 className="font-display text-4xl font-normal tracking-tight text-cg-ink">
              {docketNumber}
            </h1>
            {proceeding.status && (
              <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800">
                {proceeding.status}
              </span>
            )}
          </div>
          {proceeding.proceeding_name && (
            <p className="mt-1 text-base text-neutral-600">{proceeding.proceeding_name}</p>
          )}
          {(commentDeadlineLabel || replyDeadlineLabel) && (
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-neutral-600">
              {commentDeadlineLabel && (
                <div>
                  <span className="font-medium text-neutral-500">Comments due:</span>{' '}
                  <span className="text-cg-ink">{commentDeadlineLabel}</span>
                </div>
              )}
              {replyDeadlineLabel && (
                <div>
                  <span className="font-medium text-neutral-500">Reply due:</span>{' '}
                  <span className="text-cg-ink">{replyDeadlineLabel}</span>
                </div>
              )}
            </div>
          )}
        </div>
        <Link
          to={`/proceedings/${docketNumber}`}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-neutral-500 hover:text-neutral-800"
        >
          View full docket
          <ExternalLink className="h-3.5 w-3.5" />
        </Link>
      </div>

      <SummariesSection
        docketNumber={docketNumber}
        commentDeadline={commentDeadline}
        replyCommentDeadline={replyCommentDeadline}
        nprmCount={nprmCount}
        fetchingNprmCount={fetchingNprmCount}
        prevailingInstrument={prevailingInstrument}
        syntheticTimelineForFilings={syntheticPnTimelineForFilings}
        publicNoticeCount={publicNoticeCount}
        topPublicNoticeDocument={topPublicNoticeDocument}
      />
    </div>
  );
};
